import { useMemo } from 'react';
import { getText } from 'i18n/intl';
import { isEmpty } from 'lodash';
import { Divider, Form, Input, Select } from '@timeedit/ui-components/antd';
import type { Rule } from '@timeedit/ui-components/antd/form/index';
import { LabelWrapper } from '@timeedit/ui-components/components/InputComponent/Input';
import {
  type BulkEditFieldType,
  BulkEditFormEditValue,
  BulkEditMatchRule,
  type CommonProps,
  getBulkEditFieldOptions,
  useBulkEditFieldDisabled,
} from './common';

const TYPES_WITH_BIG_TEXTAREA = ['TEXT', 'COMMENT'];

const BulkEditString = ({ field, form }: CommonProps) => {
  const value = Form.useWatch(field.id, form);

  const options = useMemo(
    () =>
      getBulkEditFieldOptions(
        [
          'keep',
          'add',
          'findReplace',
          'replaceAll',
          'removeSpecific',
          'removeAll',
        ],
        field
      ),
    [field.multiple, field.unique, field.mandatory]
  );

  const showAdd = value === 'add' && field.multiple;
  const showFindAndReplace = value === 'findReplace';
  const showReplaceAll = value === 'replaceAll';
  const showRemoveSpecific = value === 'removeSpecific';

  const showTextArea = TYPES_WITH_BIG_TEXTAREA.includes(field.fieldType);

  const { disabled, tooltip } = useBulkEditFieldDisabled(field);

  return (
    <LabelWrapper label={field.name} tooltip={tooltip}>
      <div className="bulk-edit-input-wrapper">
        <Form.Item required name={field.id}>
          <Select defaultValue="keep" options={options} disabled={disabled} />
        </Form.Item>
        {showAdd && <Add field={field} showTextArea={showTextArea} />}
        {showFindAndReplace && (
          <FindAndReplace field={field} showTextArea={showTextArea} />
        )}
        {showReplaceAll && (
          <ReplaceAll field={field} showTextArea={showTextArea} />
        )}
        {showRemoveSpecific && (
          <RemoveSpecific field={field} showTextArea={showTextArea} />
        )}
      </div>
    </LabelWrapper>
  );
};

const useStringRules = (field: BulkEditFieldType): Rule[] => {
  return useMemo(
    () =>
      [
        {
          type: 'string',
          required: true,
          message: 'Value(s) required',
        },
        {
          max: field.length,
          message:
            field.length != null
              ? `The field does not allow values longer than ${field.length} bytes`
              : 'Value has too few characters',
        },
        !isEmpty(field.filter) && {
          validator: (_: unknown, value: string) => {
            if (value == null || value === '') {
              return Promise.resolve();
            }
            try {
              const regex = new RegExp(field.filter);
              if (!regex.test(value)) {
                return Promise.reject(
                  new Error(`Incorrect format: ${field.filter}`)
                );
              }
              return Promise.resolve();
            } catch {
              return Promise.reject(
                new Error(
                  `${field.name} contains an invalid regex (${field.filter}), please check the field settings`
                )
              );
            }
          },
        },
      ].filter(Boolean) as Rule[],
    [field.length, field.filter, field.name]
  );
};

const Add = ({
  field,
  showTextArea,
}: {
  field: BulkEditFieldType;
  showTextArea: boolean;
}) => {
  const rules = useStringRules(field);
  return (
    <BulkEditFormEditValue
      rules={rules}
      name={`${field.id}_add`}
      input={getInput(
        showTextArea,
        getText('object_manager.bulk-edit-modal.add.input-placeholder')
      )}
      allowMultiple={field.multiple}
    />
  );
};

const FindAndReplace = ({
  field,
  showTextArea,
}: {
  field: BulkEditFieldType;
  showTextArea: boolean;
}) => {
  const rules = useStringRules(field);
  return (
    <>
      <BulkEditMatchRule fieldId={field.id} isMultiple={field.multiple} />
      <BulkEditFormEditValue
        name={`${field.id}_replace`}
        rules={rules}
        input={getInput(
          showTextArea,
          getText('object_manager.bulk-edit-modal.placeholder.search')
        )}
        label={getText(
          'object_manager.bulk-edit-modal.findReplace.find-input-label'
        )}
        allowMultiple
      />
      <Divider />
      <BulkEditFormEditValue
        rules={rules}
        name={`${field.id}_with`}
        input={getInput(
          showTextArea,
          getText('object_manager.bulk-edit-modal.placeholder.search')
        )}
        label={getText(
          'object_manager.bulk-edit-modal.findReplace.replace-input-label'
        )}
        allowMultiple={field.multiple}
      />
    </>
  );
};

const ReplaceAll = ({
  field,
  showTextArea,
}: {
  field: BulkEditFieldType;
  showTextArea: boolean;
}) => {
  const rules = useStringRules(field);
  return (
    <BulkEditFormEditValue
      rules={rules}
      name={`${field.id}_replaceAll`}
      input={getInput(
        showTextArea,
        getText('object_manager.bulk-edit-modal.replaceAll.input-placeholder')
      )}
      allowMultiple={field.multiple}
    />
  );
};

const RemoveSpecific = ({
  field,
  showTextArea,
}: {
  field: BulkEditFieldType;
  showTextArea: boolean;
}) => {
  const rules = useStringRules(field);
  return (
    <BulkEditFormEditValue
      name={`${field.id}_removeSpecific`}
      rules={rules}
      input={getInput(
        showTextArea,
        getText(
          'object_manager.bulk-edit-modal.removeSpecific.input-placeholder'
        )
      )}
      allowMultiple
    />
  );
};

const getInput = (showTextArea: boolean, placeholder?: string) => {
  if (showTextArea) {
    return (
      <Input.TextArea
        className="bulk-edit-full-width"
        placeholder={placeholder}
      />
    );
  }
  return <Input className="bulk-edit-full-width" placeholder={placeholder} />;
};

export default BulkEditString;
